<template>
    <multiselect
        ref="multiselect"
        :value="value"
        @keydown.native.enter.stop
        @input="onInput"
        @tag="onAddTag"
        @search-change="onSearchChange"
        :internal-search="!hasPagination"
        :options="options"
        :placeholder="placeholder"
        :track-by="trackBy"
        :taggable="taggable"
        :multiple="multiple"
        :customLabel="getLabel"
        :maxHeight="300"
        :loading="isLoading"
        :style="{'min-width': `${minWidth}px`}">
    </multiselect>
</template>

<script>
import Vue from 'vue'
import {FieldEditorTypes, getFormFieldFormattedLabel} from '@/types/document-viewer/field/modules/editor'
import {
    checkFieldSelectOptionsHasPagination,
    FieldEditorOptionsSources,
    getApiSelectQueryStringParamName,
    getFormFieldOptions
} from '@/types/document-viewer/field/modules/editor/select'
import axios from 'axios'
import {getFieldDependencyQueryParams} from '@/types/document-viewer/field/modules/dependencies'

export default Vue.extend({
    name: 'AgGridCellEditorMultiselect',
    data() {
        return {
            value: null,
            options: [],
            isLoading: false
        }
    },
    computed: {
        field() { // FormField
            return this.params.settings
        },

        editor() {
            return this.field.editor || {}
        },

        select() {
            return this.editor.select || {}
        },

        multiple() {
            return this.editor.type === FieldEditorTypes.DD_MULTIPLE
        },

        placeholder() {
            return this.editor.placeholder
        },

        trackBy() {
            return this.select.trackBy
        },

        taggable() {
            return this.select.taggable
        },

        hasPagination() {
            return checkFieldSelectOptionsHasPagination(this.select)
        },

        queryStringParamName() {
            return getApiSelectQueryStringParamName(this.select)
        },

        attachLineData() {
            return this.select.source === FieldEditorOptionsSources.API && this.select.api && this.select.api.attachLineData
        },

        minWidth() {
            return Math.max(this.params.column.actualWidth, 120)
        }
    },
    methods: {
        getValue() {
            return this.value
        },

        isPopup() {
            return true
        },

        async onInput(options) {
            this.value = options
            await this.$nextTick()
            const {column, rowIndex} = this.params.api.getFocusedCell()
            this.params.api.setFocusedCell(rowIndex, column.getColId())
            this.params.api.stopEditing()
        },

        onAddTag(tag) {
            if (this.trackBy) {
                this.onInput({
                    [this.trackBy]: tag,
                    [this.label]: tag
                })
            } else {
                this.onInput(tag)
            }
        },

        focusInput() {
            if (this.$refs.multiselect) {
                this.$refs.multiselect.$el.focus()
            }
        },

        async fetchOptions(query = '') {
            this.isLoading = true
            const params = {}
            if (this.hasPagination) {
                params[this.queryStringParamName] = query
            }
            if (this.attachLineData) {
                params.line = this.params.data
            }
            if (this.field.dependants && this.getResourceState()) {
                params.dependencies = getFieldDependencyQueryParams({
                    field: this.field,
                    docData: this.getResourceState(),
                    lineIdx: this.params.data.lineIdx
                })
            }
            let data = []
            try {
                ({data} = await getFormFieldOptions({
                    select: this.select,
                    requestId: this.field.path + this.params.data.lineIdx,
                    params
                }))
                this.isLoading = false
            } catch (e) {
                if (!axios.isCancel(e)) {
                    this.isLoading = false
                }
            }
            this.options = data
        },

        async onSearchChange(query = '') {
            if (!this.hasPagination) { // internal search will work
                return
            }
            await this.fetchOptions(query)
        },

        getLabel(value) {
            return getFormFieldFormattedLabel({editor: this.editor, value})
        },

        getResourceState() {
            if (typeof this.params.getResourceState !== 'function') {
                return null
            }
            return this.params.getResourceState()
        }
    },
    async created() {
        this.value = this.params.value
        await this.fetchOptions()
    },
    mounted() {
        Vue.nextTick(() => {
            this.focusInput()
        })
    }
})
</script>

<style scoped>

</style>
